import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchSnapshotHtml } from '../lib/snapshots-api';

/**
 * Lazy preview thumbnail for a single snapshot.
 *
 * The list endpoint strips `html` from every item to keep the drawer
 * payload small; per-snapshot html is fetched via
 * `GET /pages/:id/snapshots/:snapshotId` when the row scrolls into view.
 * The IntersectionObserver gate defers fetches for snapshots below the
 * fold until the user actually sees them — a drawer can carry up to 20
 * snapshots and fetching every blob up front would double the cost of
 * opening the drawer.
 *
 * The iframe renders the fetched html and is CSS-scaled down so it reads
 * as a thumbnail rather than a full-size document. Failures, loading
 * states, and environments without IntersectionObserver (tests,
 * downlevel browsers) fall back to a text placeholder so the drawer
 * never shows a blank frame.
 */
export function SnapshotPreview({
  pageObjectId,
  snapshotId,
  token,
  fetchFn,
  label,
}: {
  pageObjectId: string;
  snapshotId: string;
  token: string | null;
  fetchFn: typeof fetch;
  label: string;
}) {
  const [inView, setInView] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (inView) return; // one-shot: keep true once tripped
    const el = containerRef.current;
    if (!el) return;
    // Defensive: happy-dom / jsdom test environments may not provide
    // IntersectionObserver. We remain in the placeholder state there; the
    // real browser drives the enabled gate. Tests that want to exercise
    // fetching can wire a stub observer globally.
    if (typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setInView(true);
            observer.disconnect();
            break;
          }
        }
      },
      { root: null, rootMargin: '120px', threshold: 0.01 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [inView]);

  const { data: rawHtml, isLoading, isError } = useQuery({
    queryKey: ['page-snapshot-html', pageObjectId, snapshotId],
    queryFn: () => fetchSnapshotHtml(fetchFn, pageObjectId, snapshotId, token),
    enabled: inView && Boolean(pageObjectId && snapshotId),
    staleTime: 5 * 60_000,
    retry: 0,
  });

  // The preview iframe is sandboxed with no permissions (sandbox=""), which
  // blocks script execution — that's the whole point. But Chrome still emits
  // a "Blocked script execution" console warning for every <script> tag the
  // sandbox encounters, which floods devtools when the drawer renders 5–10
  // version previews. Strip script tags up front (mirrors PageRowThumbnail's
  // scriptlessHtml). This is purely cosmetic — the sandbox is the real
  // safety boundary.
  const html = useMemo(() => {
    if (!rawHtml) return rawHtml;
    const stripped = rawHtml
      .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, '')
      .replace(/<script\b[^>]*\/?>/gi, '');
    // Inject a strict CSP meta so Chrome doesn't log a "Blocked script
    // execution" warning per script tag the regex misses or per inline
    // event handler. Behaviour is unchanged (empty sandbox already blocks
    // scripts), but the console stays quiet.
    const csp = '<meta http-equiv="Content-Security-Policy" content="script-src \'none\'">';
    const headMatch = /<head\b[^>]*>/i.exec(stripped);
    if (headMatch) {
      const insertAt = headMatch.index + headMatch[0].length;
      return stripped.slice(0, insertAt) + csp + stripped.slice(insertAt);
    }
    return csp + stripped;
  }, [rawHtml]);

  const PLACEHOLDER_WIDTH = 1200;
  const SCALE = 0.2;
  const CONTAINER_HEIGHT = 120;
  // 1 / SCALE * containerHeight so the iframe logical height, after
  // transform: scale(0.2), lands at exactly CONTAINER_HEIGHT visible px.
  const IFRAME_HEIGHT_PX = CONTAINER_HEIGHT / SCALE;

  const frameStyle = {
    width: '100%',
    height: CONTAINER_HEIGHT,
    border: '1px solid rgba(15, 23, 42, 0.06)',
    borderRadius: 6,
    background: '#f8fafc',
    overflow: 'hidden',
    position: 'relative' as const,
  };

  // Off-screen / pre-fetch placeholder. Same container element so the
  // IntersectionObserver stays attached and trips once the row scrolls
  // into view.
  if (!inView) {
    return <div ref={containerRef} style={frameStyle} aria-hidden="true" />;
  }

  if (isLoading) {
    return (
      <div
        ref={containerRef}
        style={{
          ...frameStyle,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--color-text-faint)',
          fontSize: 12,
        }}
      >
        Loading preview…
      </div>
    );
  }

  if (isError || !html) {
    return (
      <div
        ref={containerRef}
        style={{
          ...frameStyle,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--color-text-faint)',
          fontSize: 12,
        }}
      >
        Preview unavailable
      </div>
    );
  }

  return (
    <div ref={containerRef} style={frameStyle}>
      <iframe
        title={`Preview ${label}`}
        aria-hidden="true"
        sandbox=""
        srcDoc={html}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: PLACEHOLDER_WIDTH,
          height: IFRAME_HEIGHT_PX,
          border: 'none',
          pointerEvents: 'none',
          transform: `scale(${SCALE})`,
          transformOrigin: 'top left',
          background: 'var(--color-surface)',
        }}
      />
    </div>
  );
}
